import { useIntl } from '@weenat/wintl';
import isNil from 'lodash-es/isNil';
import maxBy from 'lodash-es/maxBy';
import merge from 'lodash-es/merge';
import minBy from 'lodash-es/minBy';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import { useClient } from '../../ClientProvider';
import { useItemContext } from '../../ItemContext';
import { isMetricWithHorizon } from '../../enums/MetricIds';
import TimeSteps from '../../enums/TimeSteps';
import useConvertedValue from '../../hooks/useConvertedValue';
import { mergeQueryState } from '../../mergeState';
import { AggregationType } from '../../resources/measurements.type';
import useQuery from '../../useQuery';
import { computeUntilMidnight } from '../measurements';
const MINIMUM_NUMBER_OF_FORECASTED_DAYS = 4;
export const spanToUseByDay = (day) => (day < 3 ? TimeSteps.per6Hours : TimeSteps.perDays);
export const metricsToDisplayBySpan = {
    [TimeSteps.perHours]: ['icon_code', 'RR', 'T', 'FF', 'U', 'FXY', 'DD'],
    [TimeSteps.per6Hours]: ['icon_code', 'RR', 'T', 'FF', 'U', 'FXY', 'DD'],
    [TimeSteps.perDays]: ['icon_code', 'RR', 'TN', 'TX', 'FF', 'U', 'FXY', 'DD']
};
export const useForecasts = (numberOfDays) => {
    const client = useClient();
    const { kind, id, timezone } = useItemContext();
    const sinceForFirstDays = moment().tz(timezone).startOf('day').unix();
    const beforeForFirstDays = moment().tz(timezone).add(3, 'days').startOf('day').unix();
    const sinceForOthersDays = moment().tz(timezone).add(3, 'days').startOf('day').unix();
    const beforeForOthersDays = moment()
        .tz(timezone)
        // Fallback to avoid not showing prev at all due to wrong period being sent to back-end
        .add(!isNil(numberOfDays) ? numberOfDays + 1 : MINIMUM_NUMBER_OF_FORECASTED_DAYS, 'days')
        .startOf('day')
        .unix();
    const isPlot = kind === 'plot';
    const first3DaysRequest = useQuery(client.forecasts.getForecasts({
        plotId: isPlot ? id : undefined,
        deviceId: isPlot ? undefined : id,
        since: sinceForFirstDays,
        before: beforeForFirstDays,
        span: TimeSteps.per6Hours,
        metrics: metricsToDisplayBySpan[TimeSteps.per6Hours]
    }));
    const nextDaysRequest = useQuery(client.forecasts.getForecasts({
        plotId: isPlot ? id : undefined,
        deviceId: isPlot ? undefined : id,
        since: sinceForOthersDays,
        before: beforeForOthersDays,
        span: TimeSteps.perDays,
        metrics: metricsToDisplayBySpan[TimeSteps.perDays]
    }));
    const { isLoading } = mergeQueryState(first3DaysRequest, nextDaysRequest);
    const forecasts = !isLoading ? merge(first3DaysRequest.data, nextDaysRequest.data) : undefined;
    return {
        forecasts,
        isLoading
    };
};
export const useForecastAtDay = ({ day, allowedMetrics }) => {
    const client = useClient();
    const { kind, id, timezone } = useItemContext();
    const isPlot = kind === 'plot';
    const metricsToFetch = useMemo(() => {
        let final = [...metricsToDisplayBySpan[TimeSteps.perHours]];
        if (allowedMetrics.includes('T_DEW')) {
            final = [...metricsToDisplayBySpan[TimeSteps.perHours], 'T_DEW'];
        }
        return final;
    }, [allowedMetrics]);
    const dayAsInt = !isNil(day) ? day : 0;
    const forecastAtDayRequest = useQuery(client.forecasts.getForecasts({
        plotId: isPlot ? id : undefined,
        deviceId: isPlot ? undefined : id,
        since: moment().tz(timezone).add(dayAsInt, 'days').startOf('day').unix(),
        before: moment()
            .tz(timezone)
            .add(dayAsInt + 1, 'days')
            .startOf('day')
            .unix(),
        span: TimeSteps.perHours,
        metrics: metricsToFetch
    }), { enabled: !isNil(day) });
    const forecastAtDay = forecastAtDayRequest.data;
    return { isLoading: forecastAtDayRequest.isLoading, forecastAtDay };
};
const dailyMetrics = [
    'icon_code',
    'RR',
    'TN',
    'TX',
    'phrase_32char',
    'pop_phrase'
];
const HOURLY_METRICS = ['RR', 'T'];
const TEMP_MEASUREMENTS_METRICS = ['T'];
const RAINFALL_MEASUREMENTS_METRICS = ['RR'];
export const useForecastDaySummary = (day, allowedMetrics) => {
    const client = useClient();
    const intl = useIntl();
    const { t } = intl;
    const { availableMeasures, id, kind, timezone } = useItemContext();
    const isPlot = kind === 'plot';
    const { convertValue, getUnit } = useConvertedValue();
    const isToday = day === 0;
    const startOfTheDay = moment().tz(timezone).add(day, 'days').startOf('day').unix();
    const endOfTheDay = moment().tz(timezone).add(day, 'days').add(1, 'days').startOf('day').unix();
    // Adding metrics depending on the org allowed metrics in forecasts
    const dailyMetricsToFetch = useMemo(() => {
        const finalMetrics = [...dailyMetrics];
        if (allowedMetrics.includes('etp'))
            finalMetrics.push('etp');
        return finalMetrics;
    }, [allowedMetrics]);
    const dailyForecastRequest = useQuery(client.forecasts.getForecasts({
        deviceId: isPlot ? undefined : id,
        plotId: isPlot ? id : undefined,
        metrics: dailyMetricsToFetch,
        since: startOfTheDay,
        before: endOfTheDay,
        span: TimeSteps.perDays
    }));
    const hourlyForecastRequest = useQuery(client.forecasts.getForecasts({
        deviceId: isPlot ? undefined : id,
        plotId: isPlot ? id : undefined,
        metrics: HOURLY_METRICS,
        since: startOfTheDay,
        before: endOfTheDay,
        span: TimeSteps.perHours
    }), { enabled: isToday });
    const rainfallsMeasurementsRequestParams = {
        deviceId: isPlot ? undefined : id,
        plotId: isPlot ? id : undefined,
        metrics: RAINFALL_MEASUREMENTS_METRICS,
        since: moment().tz(timezone).startOf('day').unix(),
        before: moment().tz(timezone).startOf('hour').unix(),
        span: TimeSteps.perHours
    };
    const hourlyPlotRainfallsMeasurementsRequest = useQuery(client.measurements.plots.histories.get1_1(rainfallsMeasurementsRequestParams), { enabled: isPlot && isToday });
    const hourlyDeviceRainfallsMeasurementsRequest = useQuery(client.measurements.devices.histories.get1_1(rainfallsMeasurementsRequestParams), { enabled: !isPlot && isToday });
    const hourlyRainfallsMeasurements = isPlot
        ? hourlyPlotRainfallsMeasurementsRequest.data
        : hourlyDeviceRainfallsMeasurementsRequest.data;
    const temperaturesMeasurementsRequestParams = {
        deviceId: isPlot ? undefined : id,
        plotId: isPlot ? id : undefined,
        metrics: TEMP_MEASUREMENTS_METRICS,
        since: moment().tz(timezone).startOf('day').unix(),
        before: moment().tz(timezone).startOf('minute').unix(),
        span: TimeSteps.maximum
    };
    const allPlotTemperatureMeasurementsRequest = useQuery(client.measurements.plots.histories.get1_1(temperaturesMeasurementsRequestParams), { enabled: isPlot && isToday });
    const allDeviceTemperatureMeasurementsRequest = useQuery(client.measurements.devices.histories.get1_1(temperaturesMeasurementsRequestParams), { enabled: !isPlot && isToday });
    const allTemperatureMeasurements = isPlot
        ? allPlotTemperatureMeasurementsRequest.data
        : allDeviceTemperatureMeasurementsRequest.data;
    const getDailyValuePerMetric = useCallback((metricId) => Object.values(dailyForecastRequest.data?.[metricId] || {})?.[0], [dailyForecastRequest.data]);
    // daily expected values
    const iconCodeId = getDailyValuePerMetric('icon_code');
    const dailyWeatherDescription = getDailyValuePerMetric('phrase_32char');
    const probabilityOfPrecipitation = getDailyValuePerMetric('pop_phrase');
    const expectedRainfallsValue = getDailyValuePerMetric('RR');
    const expectedMinTValue = getDailyValuePerMetric('TN');
    const expectedMaxTValue = getDailyValuePerMetric('TX');
    const etpValue = getDailyValuePerMetric('etp');
    const temperaturesForecastsByHours = hourlyForecastRequest.data?.['T'] || {};
    // hourly expected values
    const expectedHourlyMinTValue = Object.entries(temperaturesForecastsByHours).reduce((min, [timestamp, value]) => {
        if (!availableMeasures.includes('T') ||
            parseInt(timestamp, 10) >= moment().tz(timezone).startOf('hour').unix()) {
            if (!isNil(value) && (isNil(min) || value < min))
                return value;
        }
        return min;
    }, undefined);
    const expectedHourlyMaxTValue = Object.entries(temperaturesForecastsByHours).reduce((max, [timestamp, value]) => {
        if (!availableMeasures.includes('T') ||
            parseInt(timestamp, 10) >= moment().tz(timezone).startOf('hour').unix()) {
            if (!isNil(value) && (isNil(max) || value > max))
                return value;
        }
        return max;
    }, undefined);
    const expectedRainfalls = computeUntilMidnight(Object.entries(hourlyForecastRequest.data?.['RR'] || {}), timezone);
    const pastExpectedRainfalls = Object.entries(hourlyForecastRequest.data?.['RR'] || {}).reduce((sum, [timestamp, value]) => {
        if (isNil(sum))
            return value;
        // eslint-disable-next-line no-param-reassign
        if (parseInt(timestamp, 10) < moment().tz(timezone).startOf('hour').unix())
            sum += value || 0;
        return sum;
    }, null);
    // hourly measured values
    const measuredMinTDatum = minBy(allTemperatureMeasurements?.values?.T?.avg, (datum) => datum.value);
    const measuredMinTValue = measuredMinTDatum && measuredMinTDatum.value;
    const measuredMaxTDatum = maxBy(allTemperatureMeasurements?.values?.T?.avg, (datum) => datum.value);
    const measuredMaxTValue = measuredMaxTDatum && measuredMaxTDatum.value;
    const measuredRainfalls = hourlyRainfallsMeasurements?.values.RR?.sum?.reduce((sum, datum) => {
        if (isNil(sum))
            return datum.value;
        // eslint-disable-next-line no-param-reassign
        sum += datum.value || 0;
        return sum;
    }, null);
    const minT = isToday
        ? !isNil(measuredMinTValue) && !isNil(expectedHourlyMinTValue)
            ? Math.min(expectedHourlyMinTValue, measuredMinTValue)
            : expectedHourlyMinTValue
        : expectedMinTValue;
    const maxT = isToday
        ? !isNil(measuredMaxTValue) && !isNil(expectedHourlyMaxTValue)
            ? Math.max(expectedHourlyMaxTValue, measuredMaxTValue)
            : expectedHourlyMaxTValue
        : expectedMaxTValue;
    const pastRainfalls = availableMeasures.includes('RR') ? measuredRainfalls : pastExpectedRainfalls;
    const formatValuePerMetric = useCallback(({ value, metric }) => !isNil(value)
        ? convertValue({
            value,
            metric
        })
        : `\u2014 ${t(`units.${getUnit(metric)}_abbr`)}`, [convertValue, getUnit, t]);
    const capitalizeMessage = (key) => 
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    t(key, { capitalize: true });
    const aggregationsLabels = {
        min: `${capitalizeMessage('charts.min')} ${isToday ? t('periods.today_abbr') : ''}`,
        max: `${capitalizeMessage('charts.max')} ${isToday ? t('periods.today_abbr') : ''}`,
        sinceMidnight: capitalizeMessage('periods.since_midnight'),
        untilMidnight: capitalizeMessage('periods.until_midnight'),
        overTheDay: capitalizeMessage('periods.over_the_day')
    };
    /** Avoid repeating the same sentence twice */
    const finalPop = useMemo(() => {
        let final = null;
        if (!isNil(dailyWeatherDescription) && !isNil(probabilityOfPrecipitation)) {
            const loweredTrimmedPhrase = dailyWeatherDescription?.toLowerCase().trim();
            const loweredTrimmedPop = probabilityOfPrecipitation?.toLowerCase().trim();
            if (loweredTrimmedPhrase !== loweredTrimmedPop) {
                final = loweredTrimmedPop;
            }
        }
        return final;
    }, [dailyWeatherDescription, probabilityOfPrecipitation]);
    const { isLoading } = mergeQueryState(allDeviceTemperatureMeasurementsRequest, allPlotTemperatureMeasurementsRequest, hourlyDeviceRainfallsMeasurementsRequest, hourlyPlotRainfallsMeasurementsRequest, hourlyForecastRequest, dailyForecastRequest);
    return {
        aggregationsLabels,
        capitalizeMessage,
        dailyWeatherDescription,
        etpValue,
        expectedRainfalls,
        expectedRainfallsValue,
        formatValuePerMetric,
        iconCodeId,
        isLoading,
        isToday,
        maxT,
        minT,
        pastRainfalls,
        probabilityOfPrecipitation: finalPop
    };
};
export const getMetricToFetch = (metric) => {
    return metric === 'FF'
        ? [metric, 'DD', 'FXY']
        : metric === 'T_WET'
            ? ['T_WET', 'T_DRY']
            : [metric];
};
export const forecastsToMetricTuple = (metric, forecasts) => Object.entries(forecasts?.[metric] || {}).reduce((acc, [timestamp, value]) => {
    acc.push([timestamp, value]);
    return acc;
}, []);
export const measurementsToMetricTuple = (metric, measurements) => {
    if (!isMetricWithHorizon(metric)) {
        let aggregateToUse = AggregationType.avg;
        if (measurements?.values?.[metric]?.sum)
            aggregateToUse = AggregationType.sum;
        return (measurements?.values?.[metric]?.[aggregateToUse]?.reduce((acc, { timestamp, value }) => {
            acc.push([`${timestamp}`, value]);
            return acc;
        }, []) || []);
    }
    throw new Error('not implemented');
};
